import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {adminConfigurationService} from "../services/admin-config.service";
import {
    ConfigurationEntry,
    FEATURE_ACCESS_CONFIG_DEFINITIONS,
    FEATURE_ACCESS_CONFIG_ORDER,
    FeatureKey
} from "../models/admin-config.models";
import {useSnackbar} from "./SnackbarContext";
import {useTranslation} from "react-i18next";
import {useUser} from "./UserContext";
import {useAuth} from "./AuthContext";


interface FeatureConfigContextType {
    featureEntries: ConfigurationEntry[],
    isConfigLoading: boolean,
    isFeatureEnabled: (featureKey: FeatureKey) => boolean,
    updateFeatureEntryLocal: (propertyName: string, propertyValue: string) => void,
    saveFeatureConfig: (entries: ConfigurationEntry[]) => Promise<void>,
    refreshFeatureConfig: () => Promise<void>,
}

// --- Context ---

const FeatureConfigContext = createContext<FeatureConfigContextType | null>(null);

// --- Provider ---

export const FeatureConfigProvider = ({children}: {children: ReactNode}) => {
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const {isLoadingUser, user} = useUser();
    const {isAuthenticated, isVerifying, currentUserDetails} = useAuth();

    const [isConfigLoading, setIsConfigLoading] = useState(true);
    const [featureEntries, setFeatureEntries] = useState<ConfigurationEntry[]>([]);

    const fetchFeatureConfig = useCallback(async () => {
        try {
            setIsConfigLoading(true);

            const data = await adminConfigurationService.fetchGeneralConfig();

            const featureData = data
                .filter(entry => FEATURE_ACCESS_CONFIG_DEFINITIONS[entry.propertyName])
                .sort((a, b) => {
                    const indexA = FEATURE_ACCESS_CONFIG_ORDER.indexOf(a.propertyName);
                    const verifiedIndexA = indexA !== -1 ? indexA : FEATURE_ACCESS_CONFIG_ORDER.length;
                    const indexB = FEATURE_ACCESS_CONFIG_ORDER.indexOf(b.propertyName);
                    const verifiedIndexB = indexB !== -1 ? indexB : FEATURE_ACCESS_CONFIG_ORDER.length;
                    return verifiedIndexA - verifiedIndexB;
                });

            setFeatureEntries(featureData);
        } catch (error) {
            setFeatureEntries([]);
            console.error("Error fetching feature config:", error);
            showSnackbar(t('featureConfiguration.fetchDataError'), 'error');
        } finally {
            setIsConfigLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isLoadingUser || isVerifying) return;

        if (!currentUserDetails || !user) {
            setFeatureEntries([]);
            setIsConfigLoading(false);
            return;
        }

        void fetchFeatureConfig();
    }, [isLoadingUser, isVerifying, isAuthenticated, user, currentUserDetails]);
    
    const saveFeatureConfig = useCallback(async (entries: ConfigurationEntry[]) => {
        await Promise.all(
            entries.map(e => adminConfigurationService.setConfig(e.propertyName, e.propertyValue)),
        );

        await fetchFeatureConfig();
    }, [fetchFeatureConfig]);

    const updateFeatureEntryLocal = useCallback((propertyName: string, propertyValue: string) => {
        setFeatureEntries(prev =>
            prev.map(e => (e.propertyName === propertyName ? { ...e, propertyValue } : e)),
        );
    }, []);

    const isFeatureEnabled = useCallback((featureKey: FeatureKey): boolean => {
        const feature = featureEntries.filter(entry => entry.propertyName === featureKey);
        return feature.length > 0 && feature[0].propertyValue === 'true';
    }, [featureEntries]);

    const value = useMemo(
        () => ({
            featureEntries,
            isConfigLoading,
            isFeatureEnabled,
            updateFeatureEntryLocal,
            saveFeatureConfig,
            refreshFeatureConfig: fetchFeatureConfig,
        }),
        [featureEntries, fetchFeatureConfig, isConfigLoading, isFeatureEnabled, saveFeatureConfig, updateFeatureEntryLocal]
    );

    return (
        <FeatureConfigContext.Provider value={value}>
            {children}
        </FeatureConfigContext.Provider>
    );
};

// --- Hook ---

export const useFeatureConfig = () => {
    const context = useContext(FeatureConfigContext);
    if (!context) {
        throw new Error('useFeatureConfig must be used within a FeatureConfigProvider');
    }
    return context;
};
